import { useState, type FormEvent } from 'react'
import { requestPhoneChangeOtp, updateAuthProfile } from '../api/auth'
import {
  AUTH_PROFILE,
  PROFILE_FIELD,
  PROFILE_LIMITS,
  type ProfileEditPending,
  type ProfileFieldId,
} from '../constants/auth'
import { toApiMessage } from '../helpers/api-error'
import {
  onlyDigits,
  profileDisplayFields,
  profileInitials,
  toE164Phone,
} from '../helpers/auth'
import { formatDate } from '../helpers/format'
import { nationalPhoneDigits } from '../helpers/orders'
import { useAuth } from './useAuth'

export function useProfilePage() {
  const { user, displayName, updateUser } = useAuth()
  const [editing, setEditing] = useState<ProfileFieldId | null>(null)
  const [nameDraft, setNameDraft] = useState('')
  const [phoneDraft, setPhoneDraft] = useState('')
  const [otp, setOtp] = useState('')
  const [otpSentTo, setOtpSentTo] = useState<string | null>(null)
  const [pending, setPending] = useState<ProfileEditPending>(null)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  function resetPhoneOtp() {
    setOtp('')
    setOtpSentTo(null)
  }

  function startEdit(id: ProfileFieldId) {
    setEditing(id)
    setError(null)
    setMessage(null)
    resetPhoneOtp()
    if (id === PROFILE_FIELD.name) setNameDraft(user?.name ?? '')
    if (id === PROFILE_FIELD.phone) setPhoneDraft(nationalPhoneDigits(user?.phone))
  }

  function cancelEdit() {
    setEditing(null)
    setError(null)
    resetPhoneOtp()
  }

  function finishEdit(successMessage: string) {
    setEditing(null)
    resetPhoneOtp()
    setMessage(successMessage)
  }

  async function onSaveName(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const name = nameDraft.trim()
    if (!name) {
      setError(AUTH_PROFILE.nameRequired)
      return
    }
    setPending('name')
    setError(null)
    try {
      updateUser(await updateAuthProfile(name))
      finishEdit(AUTH_PROFILE.nameSaved)
    } catch (err) {
      setError(toApiMessage(err, AUTH_PROFILE.nameSaveFailed))
    } finally {
      setPending(null)
    }
  }

  function onPhoneDraftChange(value: string) {
    setPhoneDraft(onlyDigits(value, PROFILE_LIMITS.phoneDigits))
    if (otpSentTo) resetPhoneOtp()
  }

  async function sendPhoneOtp() {
    if (phoneDraft.length !== PROFILE_LIMITS.phoneDigits) {
      setError(AUTH_PROFILE.phoneInvalid)
      return
    }
    const phone = toE164Phone(phoneDraft)
    if (phone === user?.phone) {
      setError(AUTH_PROFILE.phoneUnchanged)
      return
    }
    setPending('otp-request')
    setError(null)
    try {
      await requestPhoneChangeOtp(phone)
      setOtp('')
      setOtpSentTo(phone)
    } catch (err) {
      setError(toApiMessage(err, AUTH_PROFILE.phoneOtpFailed))
    } finally {
      setPending(null)
    }
  }

  async function onSubmitPhone(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (phoneDraft.length !== PROFILE_LIMITS.phoneDigits) {
      setError(AUTH_PROFILE.phoneInvalid)
      return
    }
    const phone = toE164Phone(phoneDraft)
    if (phone === user?.phone) {
      setError(AUTH_PROFILE.phoneUnchanged)
      return
    }

    setPending('phone')
    setError(null)
    try {
      updateUser(await updateAuthProfile({ phone }))
      finishEdit(AUTH_PROFILE.phoneSaved)
    } catch (err) {
      setError(toApiMessage(err, AUTH_PROFILE.phoneSaveFailed))
    } finally {
      setPending(null)
    }
  }

  return {
    displayName,
    initials: profileInitials(user),
    memberSince: user?.createdAt ? formatDate(user.createdAt) : '',
    fields: profileDisplayFields(user),
    editing,
    busy: pending !== null,
    message,
    startEdit,
    nameForm: {
      label: AUTH_PROFILE.nameLabel,
      value: nameDraft,
      onChange: setNameDraft,
      onSubmit: (event: FormEvent<HTMLFormElement>) => void onSaveName(event),
      onCancel: cancelEdit,
      saving: pending === 'name',
      error,
    },
    phoneForm: {
      label: AUTH_PROFILE.phoneLabel,
      value: phoneDraft,
      onChange: onPhoneDraftChange,
      otp,
      onOtpChange: (value: string) => setOtp(onlyDigits(value, PROFILE_LIMITS.otpMax)),
      otpSentTo,
      onSubmit: (event: FormEvent<HTMLFormElement>) => void onSubmitPhone(event),
      onResend: () => void sendPhoneOtp(),
      onCancel: cancelEdit,
      pending,
      error,
    },
  }
}
